/**
 * API routes for ludicrous-gateway
 * Defines Express route handlers for the gateway
 */

import { Router, type Request, type Response, type NextFunction } from "express";
import type { MessagesRequest } from "../types/messages.js";
import type { GatewayConfig } from "../types/config.js";
import type { Provider, ProviderRequestContext } from "../types/providers.js";
import type { DefaultRouter } from "../routing/router.js";
import type { DefaultContentAnalyzer } from "../analyzers/content-analyzer.js";
import type { HealthTracker } from "../health/health-tracker.js";
import { createSSEProxy } from "./sse-proxy.js";
import { getMetrics } from "../utils/metrics.js";
import { getLogger } from "../utils/logger.js";
import { randomUUID } from "node:crypto";

/**
 * Format time difference as human-readable string
 */
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/**
 * Route handler context
 */
export interface RouteContext {
  config: GatewayConfig;
  providers: Map<string, Provider>;
  router: DefaultRouter;
  analyzer: DefaultContentAnalyzer;
  healthTracker: HealthTracker;
}

/**
 * Create the main API router
 */
export function createRoutes(context: RouteContext): Router {
  const router = Router();
  const logger = getLogger().child({ component: "routes" });

  // Status dashboard (HTML)
  router.get("/", (_req, res) => {
    const metrics = getMetrics().toJSON();
    const healthStatus = context.healthTracker.getHealthStatus();

    // Read version from package.json
    const version = "0.1.13";

    // Calculate uptime
    const uptimeSeconds = metrics.uptime_seconds as number;
    const hours = Math.floor(uptimeSeconds / 3600);
    const minutes = Math.floor((uptimeSeconds % 3600) / 60);
    const seconds = uptimeSeconds % 60;
    const uptimeStr = hours > 0
      ? `${hours}h ${minutes}m`
      : minutes > 0
        ? `${minutes}m ${seconds}s`
        : `${seconds}s`;

    // Calculate success rate
    let totalRequests = 0;
    let totalSuccessful = 0;
    const providers = metrics.providers as Record<string, any>;
    for (const [_, providerMetrics] of Object.entries(providers)) {
      totalRequests += providerMetrics.totalRequests || 0;
      totalSuccessful += providerMetrics.successfulRequests || 0;
    }
    const successRate = totalRequests > 0
      ? ((totalSuccessful / totalRequests) * 100).toFixed(1)
      : "0.0";

    // Status badge
    const statusColor =
      healthStatus.status === "healthy" ? "#10b981" :
      healthStatus.status === "degraded" ? "#f59e0b" : "#ef4444";
    const statusEmoji =
      healthStatus.status === "healthy" ? "●" :
      healthStatus.status === "degraded" ? "◐" : "○";

    // Build provider cards
    let providerCardsHTML = "";
    for (const [providerName, status] of healthStatus.providers) {
      const providerMetrics = providers[providerName] || { totalRequests: 0 };
      const requests = providerMetrics.totalRequests || 0;
      const statusIcon = status === "healthy" ? "✅" : status === "degraded" ? "⚠️" : "❌";
      const circuitStats = healthStatus.circuits.get(providerName);
      const circuitState = circuitStats ? circuitStats.state : "CLOSED";
      // Format circuit state: CLOSED -> Closed, HALF_OPEN -> Half-open
      const circuitDisplay = circuitState
        .split('_')
        .map((word, i) => i === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase())
        .join('-');

      providerCardsHTML += `
        <div class="provider-card">
          <div class="provider-name">${providerName}</div>
          <div class="provider-status">${statusIcon}</div>
          <div class="provider-requests">${requests.toLocaleString()} req</div>
          <div class="provider-circuit">${circuitDisplay}</div>
        </div>
      `;
    }

    // Build routing rules table
    let rulesHTML = "";
    const rules = metrics.rules as Record<string, any>;
    const ruleEntries = Object.entries(rules)
      .sort(([_, a], [__, b]) => (b.matchCount || 0) - (a.matchCount || 0));

    for (const [ruleName, ruleMetrics] of ruleEntries) {
      const matchCount = ruleMetrics.matchCount || 0;
      const lastMatch = ruleMetrics.lastMatchTime
        ? formatTimeAgo(new Date(ruleMetrics.lastMatchTime))
        : "never";

      rulesHTML += `
        <tr>
          <td>${ruleName}</td>
          <td>${matchCount.toLocaleString()}</td>
          <td class="time-ago">${lastMatch}</td>
        </tr>
      `;
    }

    if (rulesHTML === "") {
      rulesHTML = '<tr><td colspan="3" class="no-data">No routing activity yet</td></tr>';
    }

    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="refresh" content="30">
  <title>Ludicrous Gateway v${version}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Fira Code', 'SF Mono', Menlo, Monaco, 'Courier New', monospace;
      background: #0a0a0a;
      color: #e4e4e7;
      padding: 2rem;
      line-height: 1.5;
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem 2rem;
      background: linear-gradient(135deg, #18181b 0%, #27272a 100%);
      border-radius: 12px;
      border: 1px solid #3f3f46;
      margin-bottom: 2rem;
    }

    .logo {
      font-size: 1.5rem;
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .version {
      font-size: 0.875rem;
      color: #a1a1aa;
      margin-left: 1rem;
    }

    .status-badge {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: #18181b;
      border-radius: 8px;
      border: 1px solid ${statusColor};
      color: ${statusColor};
      font-size: 0.875rem;
      font-weight: 600;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      padding: 1.5rem;
      background: #18181b;
      border-radius: 12px;
      border: 1px solid #3f3f46;
    }

    .stat-label {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #a1a1aa;
      margin-bottom: 0.5rem;
    }

    .stat-value {
      font-size: 1.875rem;
      font-weight: 700;
      color: #fafafa;
    }

    .stat-unit {
      font-size: 1rem;
      color: #a1a1aa;
      margin-left: 0.25rem;
    }

    .section {
      background: #18181b;
      border-radius: 12px;
      border: 1px solid #3f3f46;
      padding: 2rem;
      margin-bottom: 2rem;
    }

    .section-title {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      color: #fafafa;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .providers-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 1rem;
    }

    .provider-card {
      padding: 1.25rem;
      background: #27272a;
      border-radius: 8px;
      border: 1px solid #3f3f46;
      text-align: center;
    }

    .provider-name {
      font-size: 0.875rem;
      font-weight: 600;
      color: #fafafa;
      margin-bottom: 0.75rem;
      text-transform: lowercase;
    }

    .provider-status {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }

    .provider-requests {
      font-size: 0.75rem;
      color: #a1a1aa;
      margin-bottom: 0.25rem;
    }

    .provider-circuit {
      font-size: 0.7rem;
      color: #71717a;
      letter-spacing: 0.05em;
    }

    table {
      width: 100%;
      border-collapse: collapse;
    }

    th {
      text-align: left;
      padding: 0.75rem;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #a1a1aa;
      border-bottom: 1px solid #3f3f46;
    }

    td {
      padding: 0.75rem;
      font-size: 0.875rem;
      border-bottom: 1px solid #27272a;
    }

    tr:last-child td {
      border-bottom: none;
    }

    .time-ago {
      color: #a1a1aa;
      font-size: 0.8125rem;
    }

    .no-data {
      text-align: center;
      color: #71717a;
      font-style: italic;
      padding: 2rem !important;
    }

    footer {
      text-align: center;
      margin-top: 2rem;
      padding: 1rem;
      color: #71717a;
      font-size: 0.75rem;
    }

    .refresh-note {
      color: #52525b;
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <div style="display: flex; align-items: center;">
        <span class="logo">LUDICROUS GATEWAY</span>
        <span class="version">v${version}</span>
      </div>
      <div class="status-badge">
        <span>${statusEmoji}</span>
        <span>${healthStatus.status}</span>
      </div>
    </header>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Uptime</div>
        <div class="stat-value">${uptimeStr}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Total Requests</div>
        <div class="stat-value">${(metrics.totalRequests as number).toLocaleString()}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Active Requests</div>
        <div class="stat-value">${metrics.activeRequests}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Success Rate</div>
        <div class="stat-value">${successRate}<span class="stat-unit">%</span></div>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Providers</h2>
      <div class="providers-grid">
        ${providerCardsHTML}
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Routing Rules</h2>
      <table>
        <thead>
          <tr>
            <th>Rule Name</th>
            <th>Matches</th>
            <th>Last Match</th>
          </tr>
        </thead>
        <tbody>
          ${rulesHTML}
        </tbody>
      </table>
    </div>

    <footer>
      <div class="refresh-note">Auto-refreshing every 30 seconds</div>
    </footer>
  </div>
</body>
</html>
    `;

    res.setHeader("Content-Type", "text/html");
    res.send(html);
  });

  // Health check endpoint
  router.get("/health", (_req, res) => {
    const healthStatus = context.healthTracker.getHealthStatus();

    const response = {
      status: healthStatus.status,
      timestamp: new Date().toISOString(),
      providers: Object.fromEntries(healthStatus.providers),
    };

    const statusCode = healthStatus.status === "healthy" ? 200 : 503;
    res.status(statusCode).json(response);
  });

  // Stats endpoint
  router.get("/stats", (_req, res) => {
    const metrics = getMetrics().toJSON();
    const healthStatus = context.healthTracker.getHealthStatus();

    res.json({
      ...metrics,
      health: {
        status: healthStatus.status,
        lastCheck: healthStatus.lastCheck.toISOString(),
      },
    });
  });

  // Debug routing endpoint
  router.post("/debug/route", async (req, res, next) => {
    try {
      const request = req.body as MessagesRequest;
      const analysis = context.analyzer.analyze(request);
      const debug = context.router.debugRoute(request, analysis);

      res.json({
        decision: debug.decision,
        analysis,
        ruleEvaluations: debug.ruleEvaluations,
      });
    } catch (error) {
      next(error);
    }
  });

  // Main messages endpoint (Anthropic-compatible)
  router.post("/v1/messages", async (req, res, next) => {
    const requestId = randomUUID();
    const requestLogger = logger.child({ requestId });
    const startTime = Date.now();

    try {
      const request = req.body as MessagesRequest;
      const isStreaming = request.stream === true;

      requestLogger.info("Incoming request", {
        model: request.model,
        streaming: isStreaming,
      });

      // Analyze content
      const analysis = context.analyzer.analyze(request);
      requestLogger.debug("Content analysis", {
        tokens: analysis.estimatedTokens,
        keywords: analysis.matchedKeywords.length,
        sensitive: analysis.hasSensitiveContent,
      });

      // Route request
      const decision = context.router.route(request, analysis);
      requestLogger.info("Routing decision", {
        provider: decision.provider,
        model: decision.model,
        rule: decision.matchedRule,
        isDefault: decision.isDefault,
      });

      // Record routing metrics
      getMetrics().recordRoutingDecision(decision);

      // Get provider
      const provider = context.providers.get(decision.provider);
      if (!provider) {
        throw new Error(`Provider not found: ${decision.provider}`);
      }

      if (!provider.enabled) {
        throw new Error(`Provider disabled: ${decision.provider}`);
      }

      // Check provider health
      if (!context.healthTracker.isProviderHealthy(decision.provider)) {
        requestLogger.warn("Provider unhealthy, checking for fallback", {
          provider: decision.provider,
        });

        // TODO: Implement fallback logic
        throw new Error(`Provider unhealthy: ${decision.provider}`);
      }

      // Build request context
      const requestContext: ProviderRequestContext = {
        headers: req.headers as Record<string, string>,
        resolvedModel: provider.resolveModel(decision.model),
        timeoutMs: context.config.server.timeout_ms,
        isStreaming,
      };

      // Record request start
      getMetrics().recordRequestStart();

      if (isStreaming) {
        // Handle streaming request
        const proxy = createSSEProxy(res);

        await provider.sendStreamingRequest(
          request,
          requestContext,
          proxy.getHandler()
        );

        const latencyMs = Date.now() - startTime;
        getMetrics().recordRequestEnd(decision.provider, true, latencyMs, true);
        context.healthTracker.recordSuccess(decision.provider);

        requestLogger.info("Streaming request completed", { latencyMs });
      } else {
        // Handle non-streaming request
        const response = await provider.sendRequest(request, requestContext);

        const latencyMs = Date.now() - startTime;
        getMetrics().recordRequestEnd(decision.provider, true, latencyMs, false);
        context.healthTracker.recordSuccess(decision.provider);

        if (response.usage) {
          getMetrics().recordTokenUsage(
            decision.provider,
            response.usage.input_tokens,
            response.usage.output_tokens
          );
        }

        requestLogger.info("Request completed", { latencyMs });
        res.json(response);
      }
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : "Unknown error";

      requestLogger.error("Request failed", {
        error: errorMessage,
        latencyMs,
      });

      // Record failure
      getMetrics().recordRequestEnd("unknown", false, latencyMs, false);

      // Pass to error handler
      next(error);
    }
  });

  return router;
}

/**
 * Error handling middleware
 */
export function errorHandler(
  err: Error,
  _req: Request,
  res: Response,
  _next: NextFunction
): void {
  const logger = getLogger();

  logger.error("Request error", { error: err.message });

  // Determine status code
  let statusCode = 500;
  let errorType = "internal_error";

  if (err.message.includes("Provider not found")) {
    statusCode = 503;
    errorType = "provider_unavailable";
  } else if (err.message.includes("Provider disabled")) {
    statusCode = 503;
    errorType = "provider_disabled";
  } else if (err.message.includes("Provider unhealthy")) {
    statusCode = 503;
    errorType = "provider_unhealthy";
  } else if (err.message.includes("API error")) {
    statusCode = 502;
    errorType = "upstream_error";
  }

  res.status(statusCode).json({
    type: "error",
    error: {
      type: errorType,
      message: err.message,
    },
  });
}

/**
 * Request logging middleware
 */
export function requestLogger(
  req: Request,
  res: Response,
  next: NextFunction
): void {
  const startTime = Date.now();
  const logger = getLogger();

  res.on("finish", () => {
    const duration = Date.now() - startTime;
    logger.info("Request", {
      method: req.method,
      path: req.path,
      status: res.statusCode,
      duration,
    });
  });

  next();
}
